import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import ForceGraph3D from '3d-force-graph';
import WebGL from 'three/examples/jsm/capabilities/WebGL';

// Helper functions
const deg2rad = deg => deg * Math.PI / 60;
const rad2deg = rad => rad * 180 / Math.PI;

export function SynapsesAnimation({ enableControls = true }) {
    const mountRef = useRef(null);
    const graphRef = useRef(null);
    const resizeTimeout = useRef(null);
    const animationInterval = useRef(null);
    const mouseX = useRef(0);
    const mouseY = useRef(0);
    const targetRotationX = useRef(0);
    const targetRotationY = useRef(0);
    const windowHalfX = useRef(window.innerWidth / 2);
    const windowHalfY = useRef(window.innerHeight / 2);
    const isDragging = useRef(false);
    
    // Check if device is mobile
    const isMobile = useMemo(() => {
        if (typeof window !== 'undefined') {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            ) || window.innerWidth < 768 || ('ontouchstart' in window);
        }
        return false;
    }, []);
    
    // Add WebGL support detection
    const isWebGLSupported = useMemo(() => {
        if (typeof window !== 'undefined') {
            return WebGL.isWebGL2Available();
        }
        return true;
    }, []);
    
    // IMPORTANT: Always disable controls on mobile
    const shouldEnableControls = enableControls && !isMobile;
    
    // Generate nodes and links for the graph - optimized for performance
    const generateGraph = () => {
        // Significantly reduced node count for better performance
        const N = isMobile ? 100 : 200; 
        
        const nodes = [...Array(N).keys()].map(i => ({
            id: i,
            val: (Math.random() * 1.5) + 1
        }));
        
        const links = [];
        // Create fewer links per node
        nodes.forEach(node => {
            // Limit to 1-2 links per node maximum
            const numNodeLinks = Math.max(1, Math.round(Math.random()));
            for (let i = 0; i < numNodeLinks; i++) {
                links.push({
                    source: node.id,
                    target: Math.round(Math.random() * (node.id > 0 ? node.id - 1 : node.id))
                });
            }
        });
        
        return { nodes, links };
    };
    
    // Mouse movement handler for subtle rotation
    const handleMouseMove = (event) => {
        if (isMobile || isDragging.current) return; // No effect during drag or on mobile
        
        // Calculate mouse position relative to the center of the window
        mouseX.current = (event.clientX - windowHalfX.current) / windowHalfX.current;
        mouseY.current = (event.clientY - windowHalfY.current) / windowHalfY.current;
        
        // Transform to rotation target (with reduced sensitivity)
        targetRotationY.current = mouseX.current * 0.1;
        targetRotationX.current = mouseY.current * 0.1;
    };
    
    // Handle wheel events to prevent zoom and allow page scrolling
    const handleWheel = (event) => {
        // Don't stop propagation - let the event bubble up for page scrolling
        // But prevent any zooming behavior in the controls
        event.stopPropagation();
        
        // Let the wheel event pass through to the page for scrolling
        return true;
    };
    
    useEffect(() => {
        if (!mountRef.current) return;
        const currentMount = mountRef.current;
        
        // Early return with fallback if WebGL not supported
        if (!isWebGLSupported) {
            renderFallbackCanvas(currentMount);
            return;
        }
        
        // Update window half refs initially
        windowHalfX.current = window.innerWidth / 2;
        windowHalfY.current = window.innerHeight / 2;
        
        // Constants
        const distance = 1000;
        let currentAngle = 0;
        
        // Generate initial graph data
        const gData = generateGraph();
        
        // Initialize the graph
        const initGraph = () => {
            // Clear previous graph if it exists
            if (graphRef.current) {
                // Clear the DOM element
                while (currentMount.firstChild) {
                    currentMount.removeChild(currentMount.firstChild);
                }
            }
            
            // First create DOM element for the canvas
            const canvasContainer = document.createElement('div');
            canvasContainer.style.width = '100%';
            canvasContainer.style.height = '100%';
            canvasContainer.style.position = 'absolute';
            canvasContainer.style.top = '0';
            canvasContainer.style.left = '0';
            canvasContainer.style.background = 'transparent';
            canvasContainer.style.backgroundColor = 'transparent';
            canvasContainer.style.backgroundImage = 'none';
            canvasContainer.style.pointerEvents = 'auto';
            canvasContainer.style.touchAction = 'pan-x pan-y'; // Explicit scroll support
            
            // Append the container to the DOM
            currentMount.appendChild(canvasContainer);
            
            // MANUALLY configure the renderer before using it
            const rendererParams = {
                antialias: true,
                alpha: true,                // Enable alpha
                premultipliedAlpha: false,  // Disable premultiplied alpha for proper transparency
                preserveDrawingBuffer: false
            };
            
            // Create custom WebGL renderer with alpha support
            const customRenderer = new THREE.WebGLRenderer(rendererParams);
            customRenderer.setClearColor(0x000000, 0);
            customRenderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
            customRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Limit pixel ratio
            customRenderer.outputEncoding = THREE.SRGBColorSpace;
            customRenderer.autoClear = false;
            
            // Style the canvas for transparency
            customRenderer.domElement.style.background = 'transparent';
            customRenderer.domElement.style.backgroundColor = 'transparent';
            customRenderer.domElement.style.width = '100%';
            customRenderer.domElement.style.height = '100%';
            customRenderer.domElement.style.position = 'absolute';
            customRenderer.domElement.style.top = '0';
            customRenderer.domElement.style.left = '0';
            customRenderer.domElement.style.pointerEvents = 'auto';
            customRenderer.domElement.style.touchAction = 'pan-x pan-y'; // Explicit scroll support
            
            // Add wheel event listener to handle scrolling correctly
            customRenderer.domElement.addEventListener('wheel', handleWheel, { passive: true });
            
            // Append our custom renderer's canvas to DOM before initializing ForceGraph
            while (canvasContainer.firstChild) {
                canvasContainer.removeChild(canvasContainer.firstChild);
            }
            canvasContainer.appendChild(customRenderer.domElement);
            
            // Create new graph using our custom renderer
            const Graph = ForceGraph3D({
                renderer: customRenderer,
                extraRenderers: [] // No extra renderers needed
            })(canvasContainer);
            
            // Configure controls for desktop
            if (shouldEnableControls) {
                // Configure cursor for dragging
                customRenderer.domElement.style.cursor = 'grab';
                
                // Setup proper orbit controls
                const controls = Graph.controls();
                if (controls) {
                    controls.enableZoom = false;
                    controls.enablePan = false;
                    controls.enableDamping = true;
                    controls.dampingFactor = 0.5;
                    controls.rotateSpeed = 0.8;
                    
                    // Disable mousewheel zooming
                    controls.mouseButtons.MIDDLE = null;
                    controls.mouseButtons.RIGHT = null;
                    controls.touches.TWO = null;
                    
                    // Completely disable the zoom handler
                    controls.handleMouseWheel = () => false;
                    
                    // Add cursor style changes on drag
                    controls.addEventListener('start', () => {
                        isDragging.current = true;
                        customRenderer.domElement.style.cursor = 'grabbing';
                    });
                    
                    controls.addEventListener('end', () => {
                        isDragging.current = false;
                        customRenderer.domElement.style.cursor = 'grab';
                    });
                }
              
                Graph.enableNavigationControls(true);
                Graph.enablePointerInteraction(true);
                Graph.enableNodeDrag(false);
            } else {
                Graph.enableNavigationControls(false);
                Graph.enablePointerInteraction(false);
                Graph.enableNodeDrag(false);
            }
            
            graphRef.current = Graph;
           
            Graph.showNavInfo(false);
            
            // Performance optimization: decrease cooldown time for physics
            Graph.cooldownTime(2000);
            
            // Performance optimization: simpler physics
            Graph.d3AlphaDecay(0.29);     // Faster physics stabilization
            Graph.d3VelocityDecay(0.18);  // Less movement over time
            
            // Set transparent background
            Graph.backgroundColor('rgba(0,0,0,0)');
            
            // Access Three.js renderer to ensure alpha is enabled and optimize
            const renderer = Graph.renderer();
            if (renderer) {
                renderer.setClearColor(0x000000, 0);
                renderer.setClearAlpha(0);
                renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Limit pixel ratio
                
                // ✅ Allow scrolling and disable pointer lock on mobile
                const canvas = renderer.domElement;
                canvas.style.touchAction = 'pan-x pan-y'; // Explicit scroll support
                canvas.style.pointerEvents = 'auto';
                canvas.addEventListener('touchstart', () => {}, { passive: true });
                canvas.addEventListener('wheel', handleWheel, { passive: true });
                
                // Critical: ensure the WebGL context has alpha
                const gl = renderer.getContext();
                gl.clearColor(0, 0, 0, 0);
            }
            
            // Access Three.js scene to remove any fog or background
            const scene = Graph.scene();
            if (scene) {
                scene.background = null;
                scene.fog = null;
            }
            
            // Set camera position
            Graph.cameraPosition({ z: distance });
            
            // Set node and link styling - simplified for performance
            Graph.nodeResolution(10);  // Lower polygon count for nodes
            Graph.nodeRelSize(6);
            Graph.nodeColor(() => '#00CF9D'); // Use the same brand color
            Graph.nodeOpacity(0.9);
            
            // Reduce link width for performance
            Graph.linkWidth(isMobile ? 2 : 2.5);
            Graph.linkColor(() => '#00CF9D'); // Use the same brand color
            
            // Reduce particle count for performance
            const particleCount = isMobile ? 1 : 1;
            Graph.linkDirectionalParticles(particleCount);
            Graph.linkDirectionalParticleWidth(isMobile ? 2 : 3);
            Graph.linkDirectionalParticleColor(() => '#00FFBF'); // Slightly lighter color for particles
            Graph.linkDirectionalParticleSpeed(0.5); // Slower particles = less CPU work
            
            // Add bloom effect with lower quality for performance
            const bloomPass = new UnrealBloomPass();
            bloomPass.strength = 0.5;  // Lower strength
            bloomPass.radius = 0.8;    // Smaller radius
            bloomPass.threshold = 2.6; // Higher threshold = fewer pixels affected
            
            const composer = Graph.postProcessingComposer();
            if (composer) {
                composer.addPass(bloomPass);
            }
            
            // Set graph data
            Graph.graphData(gData);
            
            // Fit to container size
            const width = currentMount.clientWidth;
            const height = currentMount.clientHeight;
            Graph.width(width);
            Graph.height(height);
            
            // Start camera rotation animation with mouse influence
            startCameraAnimation();
        };
        
        const startCameraAnimation = () => {
            // Clear previous interval if exists
            if (animationInterval.current) {
                clearInterval(animationInterval.current);
            }
            
            // Use a reduced rotation speed for smoother motion
            const rotationSpeed = isMobile ? 0.4 : 0.3;
            
            // Less frequent updates (30ms instead of 10ms)
            animationInterval.current = setInterval(() => {
                if (graphRef.current && !isDragging.current) {
                    currentAngle += rotationSpeed;
                
                    const baseX = distance * Math.sin(deg2rad(currentAngle));
                    const baseZ = distance * Math.cos(deg2rad(currentAngle));
                
                    // Skip mouse influence on mobile or when dragging
                    const mouseInfluenceX = isMobile ? 0 : targetRotationY.current * distance * 0.1;
                    const mouseInfluenceY = isMobile ? 0 : targetRotationX.current * distance * 0.1;
                    
                    // Only apply automatic rotation when not dragging
                    graphRef.current.cameraPosition({
                        x: baseX + mouseInfluenceX,
                        y: mouseInfluenceY,
                        z: baseZ
                    });
                }
            }, 40); // Slightly longer interval for smoother motion
        };
        
        // Initialize
        initGraph();
        
        // Handle window resize - with more aggressive throttling
        const handleResize = () => {
            if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
            
            resizeTimeout.current = setTimeout(() => {
                if (graphRef.current) {
                    const width = currentMount.clientWidth;
                    const height = currentMount.clientHeight;
                    
                    // Skip resize on zero dimensions
                    if (width === 0 || height === 0) return;
                    
                    // Update window half references for mouse movement calculations
                    windowHalfX.current = width / 2;
                    windowHalfY.current = height / 2;
                    
                    graphRef.current.width(width);
                    graphRef.current.height(height);
                    graphRef.current.refresh();
                }
            }, 200); // Longer debounce time
        };
        
        // Add event listeners for resize and mouse movement
        window.addEventListener('resize', handleResize);
        
        // Only add mousemove listener on desktop
        if (!isMobile) {
            window.addEventListener('mousemove', handleMouseMove);
        }
        
        // Clean up
        return () => {
            window.removeEventListener('resize', handleResize);            
            // Only remove mousemove listener if it was added (desktop only)
            if (!isMobile) {
                window.removeEventListener('mousemove', handleMouseMove);
            }
            
            if (animationInterval.current) {
                clearInterval(animationInterval.current);
                animationInterval.current = null;
            }
            
            if (resizeTimeout.current) {
                clearTimeout(resizeTimeout.current);
                resizeTimeout.current = null;
            }
            
            if (graphRef.current) {
                // Clean up event listeners on controls
                const controls = graphRef.current.controls?.();
                if (controls) {
                    controls.removeEventListener('start');
                    controls.removeEventListener('end');
                }
                
                const renderer = graphRef.current.renderer();
                if (renderer) {
                    // Remove wheel event listener
                    if (renderer.domElement) {
                        renderer.domElement.removeEventListener('wheel', handleWheel);
                    }
                    
                    // Properly dispose of renderer
                    renderer.dispose();
                }
                
                const scene = graphRef.current.scene();
                if (scene) {
                    // Traverse and dispose all objects in scene
                    scene.traverse((object) => {
                        if (!object) return;
                        if (object.geometry) object.geometry.dispose();
                        if (object.material) {
                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => material && material.dispose());
                            } else if (object.material.dispose) {
                                object.material.dispose();
                            }
                        }
                    });
                }
                
                // Force Graph doesn't have a specific dispose method,
                // but we can clean up the DOM element
                while (currentMount.firstChild) {
                    currentMount.removeChild(currentMount.firstChild);
                }
                graphRef.current = null;
            }
        };
    }, [isMobile, isWebGLSupported, shouldEnableControls]);
    
    // Create container with explicit transparency settings
    return (
        <div
          ref={mountRef}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            touchAction: 'pan-x pan-y', // Explicitly allow vertical and horizontal scrolling
            pointerEvents: 'auto', // Allow events to pass through
            background: 'transparent', 
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            overflow: 'hidden'
          }}
        />
      );
}

// Simple canvas fallback for browsers without WebGL
function renderFallbackCanvas(container) {
    const canvas = document.createElement('canvas');
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.background = 'transparent';
    canvas.style.touchAction = 'pan-x pan-y'; // Allow scrolling
    container.appendChild(canvas);
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 10,
        canvas.width / 2, canvas.height / 2, canvas.width / 2
    );
    
    gradient.addColorStop(0, '#00FFBF');
    gradient.addColorStop(0.9, '#00CF9D');
    gradient.addColorStop(1, 'transparent');
    
    function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2.5, 0, Math.PI * 2);
        ctx.fill();
        
        window.requestAnimationFrame(draw);
    }
    
    draw();
}
